import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Circle,
  Square,
  AlertTriangle,
  CheckCircle,
  RefreshCw
} from 'lucide-react';

interface VideoRecorderProps {
  onRecordingComplete?: (blob: Blob, metadata?: RecordingMetadata) => void;
  onStreamReady?: (stream: MediaStream, metadata?: RecordingMetadata) => void;
  maxDuration?: number; // in seconds
  showControls?: boolean;
  autoStart?: boolean;
  className?: string;
}

interface RecordingMetadata {
  audioOnly: boolean;
  cameraDisabledByCandidate?: boolean;
  videoDurationSeconds?: number;
}

const VideoRecorder: React.FC<VideoRecorderProps> = ({
  onRecordingComplete,
  onStreamReady,
  maxDuration = 300, // 5 minutes default
  showControls = true,
  autoStart = false,
  className = ''
}) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const micLevelRafRef = useRef<number | null>(null);
  const lastMicLevelUpdateRef = useRef<number>(0);

  const audioTestRecorderRef = useRef<MediaRecorder | null>(null);
  const audioTestChunksRef = useRef<Blob[]>([]);
  const micTestTimeoutRef = useRef<number | null>(null);

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [audioOnlyMode, setAudioOnlyMode] = useState(false);

  const [micLevel, setMicLevel] = useState(0);
  const [isMicTestRecording, setIsMicTestRecording] = useState(false);
  const [micTestUrl, setMicTestUrl] = useState<string | null>(null);

  // Initialize camera — tries video+audio first, falls back to audio-only
  const initializeCamera = useCallback(async () => {
    // First attempt: video + audio together
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true
        }
      });

      setStream(mediaStream);
      streamRef.current = mediaStream;
      setPermissionGranted(true);
      setAudioOnlyMode(false);
      setError(null);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }

      if (onStreamReady) {
        onStreamReady(mediaStream, { audioOnly: false });
      }

      if (autoStart) {
        startRecording(mediaStream);
      }
      return;
    } catch (videoAudioErr: any) {
      console.warn('Video+audio failed, trying audio-only:', videoAudioErr.name);
    }

    // Second attempt: audio only (camera broken/unavailable, mic works)
    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: {
          echoCancellation: true,
          noiseSuppression: true
        }
      });

      setStream(audioStream);
      streamRef.current = audioStream;
      setPermissionGranted(true);
      setAudioOnlyMode(true);
      setVideoEnabled(false);
      setError(null);

      if (onStreamReady) {
        onStreamReady(audioStream, { audioOnly: true });
      }

      if (autoStart) {
        startRecording(audioStream);
      }
      return;
    } catch (audioErr: any) {
      console.error('Audio-only also failed:', audioErr);

      // Both failed — show error with helpful options
      if (audioErr.name === 'NotAllowedError') {
        setError(t('videoRecorder.permissionDenied'));
      } else if (audioErr.name === 'NotFoundError') {
        setError(t('videoRecorder.deviceNotFound'));
      } else {
        setError(t('videoRecorder.genericError'));
      }
    }
  }, [autoStart, onStreamReady, t]);

  useEffect(() => {
    initializeCamera();

    return () => {
      // Cleanup
      if (micTestTimeoutRef.current) {
        window.clearTimeout(micTestTimeoutRef.current);
        micTestTimeoutRef.current = null;
      }

      if (audioTestRecorderRef.current && audioTestRecorderRef.current.state !== 'inactive') {
        try {
          audioTestRecorderRef.current.stop();
        } catch {
        }
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      if (micLevelRafRef.current) {
        cancelAnimationFrame(micLevelRafRef.current);
        micLevelRafRef.current = null;
      }
      analyserRef.current = null;
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {
        });
        audioContextRef.current = null;
      }

      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!stream) return;

    if (stream.getAudioTracks().length === 0) {
      setMicLevel(0);
      return;
    }

    try {
      const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextCtor) return;

      const audioCtx: AudioContext = new AudioContextCtor();
      audioContextRef.current = audioCtx;

      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      analyserRef.current = analyser;

      const buffer = new Float32Array(analyser.fftSize);

      const tick = () => {
        const analyserNode = analyserRef.current;
        const ctx = audioContextRef.current;
        if (!analyserNode || !ctx) return;

        if (ctx.state === 'suspended') {
          ctx.resume().catch(() => {});
        }

        analyserNode.getFloatTimeDomainData(buffer);

        let sum = 0;
        for (let i = 0; i < buffer.length; i++) {
          const x = buffer[i];
          sum += x * x;
        }
        const rms = Math.sqrt(sum / buffer.length);
        const normalized = Math.min(1, rms * 3);

        const now = performance.now();
        if (now - lastMicLevelUpdateRef.current > 80) {
          lastMicLevelUpdateRef.current = now;
          setMicLevel(normalized);
        }

        micLevelRafRef.current = requestAnimationFrame(tick);
      };

      micLevelRafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      console.warn('Mic level meter init failed:', e);
    }

    return () => {
      if (micLevelRafRef.current) {
        cancelAnimationFrame(micLevelRafRef.current);
        micLevelRafRef.current = null;
      }
      analyserRef.current = null;
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
      setMicLevel(0);
    };
  }, [stream]);

  useEffect(() => {
    return () => {
      if (micTestUrl) {
        URL.revokeObjectURL(micTestUrl);
      }
    };
  }, [micTestUrl]);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    }

    setIsRecording(false);
    setIsPaused(false);

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startRecording = useCallback((mediaStream?: MediaStream) => {
    const streamToUse = mediaStream || stream;
    if (!streamToUse) return;

    chunksRef.current = [];

    const hasVideo = streamToUse.getVideoTracks().length > 0;
    const mimeType = hasVideo ? 'video/webm;codecs=vp9,opus' : 'audio/webm;codecs=opus';
    let recorder: MediaRecorder;

    try {
      recorder = new MediaRecorder(streamToUse, { mimeType });
    } catch (e) {
      try {
        recorder = new MediaRecorder(streamToUse, { mimeType: hasVideo ? 'video/webm' : 'audio/webm' });
      } catch (e2) {
        recorder = new MediaRecorder(streamToUse);
      }
    }

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    recorder.onstop = () => {
      const blobType = hasVideo ? 'video/webm' : 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: blobType });
      if (onRecordingComplete) {
        onRecordingComplete(blob, {
          audioOnly: !hasVideo,
          cameraDisabledByCandidate: !hasVideo,
          videoDurationSeconds: recordingTime,
        });
      }
    };

    mediaRecorderRef.current = recorder;
    recorder.start(1000); // Collect data every second
    setIsRecording(true);
    setRecordingTime(0);

    // Start timer
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => {
        if (prev >= maxDuration - 1) {
          stopRecording();
          return prev;
        }
        return prev + 1;
      });
    }, 1000);
  }, [stream, maxDuration, onRecordingComplete, stopRecording]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
      } else {
        mediaRecorderRef.current.pause();
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      }
      setIsPaused(!isPaused);
    }
  }, [isRecording, isPaused]);

  const toggleVideo = useCallback(() => {
    if (stream) {
      stream.getVideoTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setVideoEnabled(!videoEnabled);
    }
  }, [stream, videoEnabled]);

  const toggleAudio = useCallback(() => {
    if (stream) {
      stream.getAudioTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setAudioEnabled(!audioEnabled);
    }
  }, [stream, audioEnabled]);

  const startMicTest = useCallback(() => {
    if (isMicTestRecording) return;
    if (isRecording) return;
    if (!audioEnabled) return;

    const currentStream = streamRef.current || stream;
    if (!currentStream) return;

    const audioTracks = currentStream.getAudioTracks();
    if (audioTracks.length === 0) return;

    setMicTestUrl(null);
    setIsMicTestRecording(true);

    audioTestChunksRef.current = [];

    const audioOnlyStream = new MediaStream([audioTracks[0]]);

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(audioOnlyStream, { mimeType: 'audio/webm;codecs=opus' });
    } catch (e) {
      recorder = new MediaRecorder(audioOnlyStream);
    }

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioTestChunksRef.current.push(event.data);
      }
    };

    recorder.onstop = () => {
      const blob = new Blob(audioTestChunksRef.current, { type: 'audio/webm' });
      const url = URL.createObjectURL(blob);
      setMicTestUrl(url);
      setIsMicTestRecording(false);
    };

    audioTestRecorderRef.current = recorder;
    recorder.start();

    micTestTimeoutRef.current = window.setTimeout(() => {
      if (audioTestRecorderRef.current && audioTestRecorderRef.current.state !== 'inactive') {
        try {
          audioTestRecorderRef.current.stop();
        } catch {
        }
      }
      micTestTimeoutRef.current = null;
    }, 2500);
  }, [audioEnabled, isMicTestRecording, isRecording, stream]);

  const retryCamera = useCallback(() => {
    setError(null);
    initializeCamera();
  }, [initializeCamera]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const timeRemaining = maxDuration - recordingTime;
  const isNearEnd = timeRemaining <= 30;
  const micLevelPct = Math.round(micLevel * 100);
  const micLevelColor = micLevelPct > 66 ? 'bg-red-500' : micLevelPct > 33 ? 'bg-yellow-400' : 'bg-green-500';
  const canStartMicTest = permissionGranted && audioEnabled && !isRecording && !isMicTestRecording;

  if (error) {
    return (
      <div className={`bg-gray-900 rounded-lg p-6 text-center ${className}`}>
        <AlertTriangle size={48} className="mx-auto text-red-500 mb-4" />
        <p className="text-white mb-4">{error}</p>
        <button
          onClick={retryCamera}
          className="btn-primary flex items-center gap-2 mx-auto"
        >
          <RefreshCw size={18} />
          {t('videoRecorder.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className={`relative bg-gray-900 rounded-lg overflow-hidden ${className}`}>
      {/* Video Preview — only if we have video tracks */}
      {audioOnlyMode ? (
        <div className="w-full h-full flex items-center justify-center bg-gray-900 min-h-[200px]">
          <div className="text-center">
            <Mic size={64} className="mx-auto text-lotus-gold mb-4" />
            <p className="text-white text-lg font-medium mb-1">
              {t('videoRecorder.audioOnlyMode')}
            </p>
            <p className="text-gray-400 text-sm">
              {t('videoRecorder.audioOnlyHint')}
            </p>
          </div>
        </div>
      ) : (
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="w-full h-full object-cover"
        />
      )}

      {/* Camera off overlay */}
      {!videoEnabled && !audioOnlyMode && (
        <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
          <div className="text-center">
            <VideoOff size={48} className="mx-auto text-gray-400 mb-2" />
            <p className="text-gray-400">{t('videoRecorder.cameraOff')}</p>
          </div>
        </div>
      )}

      {/* Recording indicator */}
      {isRecording && (
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${isPaused ? 'bg-yellow-500' : 'bg-red-500 animate-pulse'}`} />
          <span className={`text-white font-mono ${isNearEnd ? 'text-red-400' : ''}`}>
            {formatTime(recordingTime)} / {formatTime(maxDuration)}
          </span>
        </div>
      )}

      {/* Permission status */}
      {permissionGranted && !isRecording && (
        <div className="absolute top-4 left-4 flex items-center gap-2 text-green-400">
          <CheckCircle size={16} />
          <span className="text-sm">{t('videoRecorder.ready')}</span>
        </div>
      )}

      {permissionGranted && (
        <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2 text-white">
          <div className="flex items-center gap-2">
            {audioEnabled ? (
              <Mic size={16} className="text-white" />
            ) : (
              <MicOff size={16} className="text-white" />
            )}
            <div className="w-24 h-2 bg-gray-700 rounded overflow-hidden">
              <div
                className={`h-2 ${micLevelColor}`}
                style={{ width: `${audioEnabled ? micLevelPct : 0}%` }}
              />
            </div>
            <span className="text-xs tabular-nums w-10 text-right">{audioEnabled ? `${micLevelPct}%` : '0%'}</span>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={startMicTest}
              disabled={!canStartMicTest}
              className={`px-2 py-1 rounded text-xs transition-colors ${
                canStartMicTest ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-800 text-gray-400 cursor-not-allowed'
              }`}
              title={t('videoRecorder.micTestTitle')}
            >
              {isMicTestRecording ? t('videoRecorder.micTesting') : t('videoRecorder.micTest')}
            </button>
            {micTestUrl && !isMicTestRecording && (
              <audio controls src={micTestUrl} className="h-8" />
            )}
          </div>
        </div>
      )}

      {/* Controls */}
      {showControls && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3">
          {/* Video toggle — hidden in audio-only mode */}
          {!audioOnlyMode && (
            <button
              onClick={toggleVideo}
              className={`p-3 rounded-full transition-colors ${
                videoEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-600 hover:bg-red-500'
              }`}
              title={videoEnabled ? t('videoRecorder.turnOffCamera') : t('videoRecorder.turnOnCamera')}
            >
              {videoEnabled ? <Video size={20} className="text-white" /> : <VideoOff size={20} className="text-white" />}
            </button>
          )}

          {/* Record/Stop button */}
          {!isRecording ? (
            <button
              onClick={() => startRecording()}
              className="p-4 bg-red-600 hover:bg-red-500 rounded-full transition-colors"
              title={t('videoRecorder.startRecording')}
            >
              <Circle size={24} className="text-white fill-white" />
            </button>
          ) : (
            <button
              onClick={stopRecording}
              className="p-4 bg-red-600 hover:bg-red-500 rounded-full transition-colors"
              title={t('videoRecorder.stopRecording')}
            >
              <Square size={24} className="text-white fill-white" />
            </button>
          )}

          {/* Audio toggle */}
          <button
            onClick={toggleAudio}
            className={`p-3 rounded-full transition-colors ${
              audioEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-600 hover:bg-red-500'
            }`}
            title={audioEnabled ? t('videoRecorder.muteAudio') : t('videoRecorder.unmuteAudio')}
          >
            {audioEnabled ? <Mic size={20} className="text-white" /> : <MicOff size={20} className="text-white" />}
          </button>
        </div>
      )}

      {/* Time warning */}
      {isRecording && isNearEnd && (
        <div className="absolute top-20 right-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm animate-pulse">
          {t('videoRecorder.timeWarning', { seconds: timeRemaining })}
        </div>
      )}
    </div>
  );
};

export default VideoRecorder;
